import { Injectable, computed, signal } from '@angular/core';
import { CommonField, CommonFieldConfig, CommonFieldValue, FieldAndValue } from './field';


@Injectable()
export abstract class CommonFieldWidgetBase<T, FIELD extends CommonField<unknown, T>> {

	public readonly fieldAndValue = signal<FieldAndValue<T, FIELD>>(undefined!);
	public readonly hasFocus = signal(false);
	public readonly hadFocus = signal(false);
	public readonly randomId = `field-${Math.floor(Math.random() * (2 ** 32))}`;
	public readonly formFieldName = computed(() => this.fieldAndValue()?.field.formFieldName ?? '');
	public readonly isEnabled = computed(() => {
		const fv = this.fieldAndValue();
		const config = fv!.field.config()! as CommonFieldConfig;
		if (fv?.field) return fv.field.saving() || (config.isEnabled ? config.isEnabled() : true);
		return true;
	});


	/**
	 * This must be called from the suclassed widget in the ngOnInit() lifecycle
	 * method.  It passes in the input field to this base class.
	 */
	protected onInit(field: FIELD) {

		const value = this.calculateFieldValue(field);
		this.fieldAndValue.set({ field, value });
		field.form.addFieldWidget(this);
	}


	/**
	 * Return the current error string if there is one and
	 * the showError property was calculated to true.
	 */
	public readonly errorToShow = computed(() => {
		const fv = this.fieldAndValue();
		if (!fv) return '';
		if (fv.value.showError) return fv.value.error;
		return '';
	});


	/**
	 * Return the current error string if there is one.
	 */
	public readonly error = computed(() => {
		const fv = this.fieldAndValue();
		if (!fv) return '';
		return fv.value.error;
	});


	/**
	 * Determine if two values are equivelant.  The default implementation
	 * works for primitive data types.  Field widgets with complex T values
	 * should override this.
	 */
	protected valuesAreSame(value1: T, value2: T) {
		return value1 == value2;
	}


	/**
	 * Field widgets may override this function to coerce the user input
	 * into valid data that is suitable for validation and persistence.
	 */
	protected cleanValue(value: T): T {
		return value;
	}


	/**
	 * Field values MUST have a validate method that checks the value and
	 * determines if there is an error.  The value will have already been
	 * cleaned using the cleanValue() method.
	 * 
	 * The textual error message should be returned or '' if no error.
	 */
	protected abstract validate(value: T): string;


	/**
	 * Processes the field value. If valid, return an object
	 * with the formFieldName as a property with the value.
	 */
	public getValidValue() {

		this.updateFieldAndValue();

		const fieldAndValue = this.fieldAndValue();
		if (!fieldAndValue) return false;

		const { field, value } = fieldAndValue;

		if (value.error) return undefined;
		else {
			const retval: { [name: string]: T } = {};
			retval[field.formFieldName] = value.value;
			return retval;
		};
	}


	/**
	 * Fields must have a setFocus method that sets the browser focus.
	 */
	public abstract setFocus(): void;


	/**
	 * Field widgets should call this whenever the UI receives focus.
	 */
	public onFocus() {
		this.hasFocus.set(true);
	}


	/**
	 * Field widgets MUST have an onChange() method that is called
	 * whenever the value changes.  This method MUST call the base
	 * processChange() method and optionally perform other tasks.
	 */
	public abstract onChange(...args: any[]): void;


	/**
	 * Processing when the field value changes. Field widgets
	 * should call this from an onChange() event.
	 */
	protected async processChange(value: T) {

		const fv = this.fieldAndValue();

		if (!fv) {
			console.error('CommonFieldWidgetBase.processChange() called but the fieldAndValue variable was not already set.');
		}
		else {
			fv.field.pendingValue.set(value);
			this.updateFieldAndValue();
		}

	}


	/**
	 * Field widgets MUST have an onBlur() method that is called
	 * whenever the field loses focus.  This method MUST call the
	 * base processBlur() method and optionally perform other tasks.
	 */
	public abstract onBlur(): Promise<void>;


	/**
	 * Processing when the field loses focus. Field widgets
	 * should call this from an onBlur() event.
	 */
	protected async processBlur() {

		this.hadFocus.set(true);
		this.hasFocus.set(false);

		//
		// Recalculate the value because the hadFocus.set
		// might result in a different showError value.
		//
		this.updateFieldAndValue();

		await this.individualSave();
	}


	/**
	 * Update the fieldAndValue signal with the latest value for the current field.
	 */
	private updateFieldAndValue() {

		let fv = this.fieldAndValue();

		if (!fv) {
			console.error('CommonFieldWidgetBase.updateFieldAndValue() called but the fieldAndValue variable was not already set.');
		}
		else {

			const v1 = fv.value;
			const v2 = this.calculateFieldValue(fv.field);

			if (
				v1.value !== v2.value ||
				v1.error !== v2.error ||
				v1.showError !== v2.showError ||
				v1.isChanged !== v2.isChanged
			) {
				fv = { ...fv, value: this.calculateFieldValue(fv.field) };
				this.fieldAndValue.set(fv);
			}
		}
	}


	/**
	 * Checks any pending value, cleans and validates it, and then stores
	 * the value and related details into an object.
	 */
	private calculateFieldValue(field: FIELD): CommonFieldValue<T> {

		const actualValue = field.actualValue();
		const pendingValue = field.pendingValue();

		//
		// Start off with a result initialized with the actualValue
		//
		const result: CommonFieldValue<T> = {
			value: actualValue,
			isChanged: false,
			error: '',
			showError: false,
		};

		//
		// Clean and validate the pending value, if there is one
		//
		if (pendingValue !== null) {

			result.value = this.cleanValue(pendingValue);

			//
			// Checking for sameness happens after the cleaning, because the cleaning
			// could change a different pending value to be the same as the actual.
			// e.g. The case or trimming could change the text to match the actual.
			//
			if (this.valuesAreSame(actualValue, result.value) == false) {

				result.isChanged = true;

			}
		}

		//
		// We now check for a validation error.  Note that is possible for the actual
		// value to have a validation error, too.
		//
		result.error = this.validate(result.value);
		const showErrors = this.hadFocus() || field.form.forceShowErrors();
		result.showError = result.error !== '' && showErrors;

		return result;
	}


	/**
	 * Determine if the pendingValue can be saved and, if so,
	 * call the field's save method.  Otherwise, record the
	 * validation error text.
	 */
	private async individualSave() {

		const fv = this.fieldAndValue();

		if (!fv) {
			console.error('CommonFieldWidgetBase.individualSave() called but the fieldAndValue variable was not already set.');
			return;
		}

		const { field, value } = fv;

		if (!field.individualSave) {
			if (!field.form.hasSubmitButton()) {
				console.error(`Form field ${field.formFieldName} does not have an individualSave function and the form is not configured with a submit button.`);
			}

			return;	// No individualSave function provided
		}

		if (value.isChanged == false) return;	// Nothing to save
		if (value.error !== '') return;			// Don't save with error

		//
		// Save
		//
		try {
			field.saving.set(true);
			await field.individualSave(value.value);

			//
			// DO NOT REMOVE THIS.
			// The change should flow through the actualValue signal when 
			// the save() function is called.
			//
			this.reset();
		}
		catch (e) {
			console.error('CommonFieldWidgetBase.save() failed.', e);

			this.fieldAndValue.set({
				field,
				value: {
					...value,
					error: 'Could Not Save',
					showError: true,
				}
			});
		}

		field.saving.set(false);

	}


	/**
	 * Remove any ending pendingValue and recalculate. 
	 */
	public reset() {

		const fv = this.fieldAndValue();
		if (fv) {
			fv.field.pendingValue.set(null);
			this.hadFocus.set(false);
			this.updateFieldAndValue();
		}
	}


	protected getValue(field: FIELD) {
		const pendingValue = field.pendingValue();
		return pendingValue === null ? field.actualValue() : pendingValue;
	}


	protected render(value: T): T | string {
		return value;
	}



	public isExcluded = computed(() => {

		let excluded = false;

		const { field } = this.fieldAndValue();
		if (!field) excluded = true;

		else {
			//
			// If we don't have the signal, then it is not excluded
			//
			if (!field.isExcluded) excluded = false;

			else excluded = field.isExcluded() ? true : false;
		}

		return excluded;
	});
}